import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  Currency,
  FiatWalletAccountState,
  JonumVerificationStatus,
} from '#src/__generated__/globalTypes.ts';
import { RadioGroup } from '#src/atoms/inputs/RadioGroup/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS } from '#src/atoms/typography/index.tsx';
import { Alert } from '#src/atoms/ui/Alert/index.tsx';
import { NeedsValidatedFiatWalletHelper } from '#src/components/fiatWallet/NeedsValidatedFiatWalletHelper/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useCryptoBalance } from '#src/hooks/useCryptoBalance.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { EnterCashWalletKYCFlow_Source } from '#src/protos/events/platform/web/events.ts';

import { useUpdateRewardCurrency } from './useUpdateRewardCurrency.ts';

const Group = styled(Vertical)`
  padding-right: var(--double-unit);
  width: 100%;
`;

const messages = defineMessages({
  title: {
    defaultMessage: 'Competition rewards',
  },
  field: {
    defaultMessage: 'Receive rewards in',
  },
  jonumBannerTitle: {
    defaultMessage: 'Rewards will be paid in crypto',
  },
  jonumBannerMessage: {
    defaultMessage:
      'Following French regulation, your rewards must be received in crypto.',
  },
});

export const RewardCurrency = () => {
  const {
    currentUser,
    walletPreferences: { showCryptoWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canDepositAndWithdraw, fiatCurrency } = useFiatBalance();
  const { cryptocurrencySymbol } = useCryptoBalance();
  const rewardCurrency =
    currentUser?.userSettings?.rewardCurrency || Currency.ETH;

  const { updateRewardCurrency } = useUpdateRewardCurrency();

  const onChange = (value: Currency) => {
    updateRewardCurrency(value);
  };

  if (!currentUser) return null;

  const { jonumVerification } = currentUser;
  const isJonumSubject =
    !!jonumVerification &&
    jonumVerification.status !== JonumVerificationStatus.EXEMPT;

  const options = [
    {
      value: Currency.FIAT,
      label: (
        <Group>
          <BodyM color="var(--c-white)">{fiatCurrency}</BodyM>
          <BodyS color="var(--c-nd-600)">
            <FormattedMessage defaultMessage="Rewards will be added to your Cash Wallet" />
          </BodyS>
        </Group>
      ),
      helper: (
        <NeedsValidatedFiatWalletHelper
          canListAndTradeHelper={
            <FormattedMessage defaultMessage="To receive cash rewards you must verify your identity by adding a government ID." />
          }
          defaultHelper={
            <FormattedMessage defaultMessage="To receive cash rewards you must activate your Cash Wallet and verify your identity by adding a government ID." />
          }
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          withPadding
          source={
            EnterCashWalletKYCFlow_Source.SETTINGS_CASH_COMPETITION_REWARDS_HELPER
          }
        />
      ),
      disabled: !canDepositAndWithdraw,
    },
    {
      value: Currency.ETH,
      label: <BodyM color="var(--c-white)">{cryptocurrencySymbol}</BodyM>,
    },
  ];
  if (!showCryptoWallet || !showFiatWallet) return null;

  const filteredOptions = isJonumSubject
    ? options.filter(o => o.value !== Currency.FIAT)
    : options;

  return (
    <SettingsSection title={messages.title}>
      {isJonumSubject ? (
        <Alert
          title={<FormattedMessage {...messages.jonumBannerTitle} />}
          message={<FormattedMessage {...messages.jonumBannerMessage} />}
        />
      ) : null}
      <RadioGroup
        withSpacing
        options={filteredOptions}
        value={rewardCurrency}
        name="currency"
        onChange={onChange}
      />
    </SettingsSection>
  );
};
